/**
 * Módulo Visor CFDI SAT — pestañas definidas en un solo registro.
 * Sin `component` → ModuloPlaceholder (misma lógica visual del sistema).
 */
import { Navigate } from "react-router-dom";
import PanelGeneral from "@/pages/PanelGeneral";
import Clientes from "@/pages/Clientes";
import CargaInformacion from "@/pages/CargaInformacion";
import VisorCFDI from "@/pages/VisorCFDI";
import ConfrontaCfdiHub from "@/pages/ConfrontaCfdiHub";
import AuditoriaEfos from "@/pages/AuditoriaEfos";
import AuditoriaEstatusLocal from "@/pages/AuditoriaEstatusLocal";
import Ayuda from "@/pages/Ayuda";
import VisoresSatHub from "@/pages/VisoresSatHub";
import DescargaAutomaticaSat from "@/pages/DescargaAutomaticaSat";
import DocumentosFiscalesSat from "@/pages/DocumentosFiscalesSat";
import MonitorSincronizacionSat from "@/pages/MonitorSincronizacionSat";
import Contabilidad from "@/pages/Contabilidad";
import GestionDatos from "@/pages/GestionDatos";
import DatosMaestrosHub from "@/pages/DatosMaestrosHub";
import DatosMaestrosValidadorCheckIdPage from "@/pages/datosMaestros/DatosMaestrosValidadorCheckIdPage";
import DatosMaestrosValidacionRfcSatPage from "@/pages/datosMaestros/DatosMaestrosValidacionRfcSatPage";
import { VALIDADOR_DATOS_CHECK_ID_LABEL } from "@/lib/checkIdLabels";
import {
  DATOS_MAESTROS_CHECK_ID_PATH,
  DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
} from "@/modules/datosMaestros/datosMaestrosHub";
import Configuracion from "@/pages/Configuracion";
import { CONFIGURACION_DESPACHO_PATH } from "@/modules/config/configuracionRoutes";
import ModuleHubInicio from "@/pages/modulos/ModuleHubInicio";
import { buildModuloFromTabs, createModuloTab } from "@/modules/moduloTabRegistry";
import { VISOR_MODULE_CARD_TITLE, CONCILIACION_MANUAL_CFDI_TITLE } from "@/modules/workModuleCore";

function VisorModuloInicio() {
  return (
    <ModuleHubInicio
      moduleId={MODULO_VISOR_ID}
      title={CONTEXT_LABEL}
      getHubCards={() => MODULO_VISOR.getHubCards()}
    />
  );
}

export const MODULO_VISOR_ID = "visor_sat";

export const MODULO_VISOR_CARD = {
  id: MODULO_VISOR_ID,
  title: VISOR_MODULE_CARD_TITLE,
  description: "Panel, clientes, carga, descarga de CFDI SAT, visor XML, confronta y auditoría EFOS.",
};

const CONTEXT_LABEL = VISOR_MODULE_CARD_TITLE;

/** Registro maestro: sidebar + rutas se generan desde aquí. */
const VISOR_TABS = [
  createModuloTab({ index: true, path: "/", label: "Panel General", iconKey: "panel", end: true, testid: "nav-panel-general", component: <PanelGeneral /> }),
  createModuloTab({
    path: "/visor/inicio",
    label: "Inicio del módulo",
    iconKey: "panel",
    testid: "nav-visor-inicio",
    component: <VisorModuloInicio />,
  }),
  createModuloTab({
    path: "/datos-maestros",
    label: "Datos Maestros",
    iconKey: "database",
    description:
      "Catálogo fiscal compartido y utilidades SAT base (empresas, CSF, descarga e integración).",
    testid: "nav-datos-maestros",
    hubCard: false,
    component: <DatosMaestrosHub />,
  }),
  createModuloTab({
    path: DATOS_MAESTROS_CHECK_ID_PATH,
    label: VALIDADOR_DATOS_CHECK_ID_LABEL,
    iconKey: "validate",
    showInNav: false,
    hubOnlyNav: true,
    hubCard: false,
    testid: "nav-datos-maestros-check-id",
    component: <DatosMaestrosValidadorCheckIdPage />,
  }),
  createModuloTab({
    path: DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
    label: "Validación de RFC SAT",
    iconKey: "validate",
    showInNav: false,
    hubOnlyNav: true,
    hubCard: false,
    testid: "nav-datos-maestros-validacion-rfc-sat",
    component: <DatosMaestrosValidacionRfcSatPage />,
  }),
  createModuloTab({
    path: "/clientes",
    label: "Clientes",
    iconKey: "clients",
    description: "Alta y administración de contribuyentes, RFC, certificados FIEL y datos fiscales por cliente.",
    hubOnlyNav: true,
    component: <Clientes />,
  }),
  createModuloTab({
    path: "/carga-informacion",
    label: "Carga de Información CFDI SAT",
    iconKey: "upload",
    description: "Suba XML, Excel, CSV o integraciones para alimentar el visor y la confronta contable.",
    component: <CargaInformacion />,
  }),
  createModuloTab({
    path: "/descarga-automatica-sat",
    label: "Descarga de CFDI SAT",
    iconKey: "cloud",
    description:
      "Descargue CFDI del portal SAT con la e.Firma del cliente por periodo (emitidos y recibidos). Rescate manual; el día a día lo cubre la sync en Clientes.",
    testid: "nav-descarga-automatica-sat",
    hubCard: false,
    component: <DescargaAutomaticaSat />,
  }),
  createModuloTab({
    path: "/documentos-fiscales-sat",
    label: "CSF y opinión SAT",
    iconKey: "validate",
    description:
      "Dueño técnico: constancia CSF y opinión de cumplimiento con la e.firma del cliente (pantalla canónica; no es descarga de XML).",
    testid: "nav-documentos-fiscales-sat",
    hubCard: false,
    component: <DocumentosFiscalesSat />,
  }),
  createModuloTab({
    path: "/monitor-sincronizacion-sat",
    label: "Estado sincronización SAT",
    iconKey: "cloud",
    description:
      "Consulte si la descarga automática terminó bien, revise la bitácora por cliente y el resumen de CFDI almacenados.",
    testid: "nav-monitor-sincronizacion-sat",
    hubOnlyNav: true,
    hubCard: false,
    component: <MonitorSincronizacionSat />,
  }),
  createModuloTab({
    path: "/confronta-cfdi",
    label: "Confronta de CFDI SAT",
    iconKey: "compare",
    description: "Compare CFDI del SAT contra contabilidad Excel y detecte diferencias por periodo.",
    component: <ConfrontaCfdiHub />,
  }),
  createModuloTab({
    path: "/visor",
    label: "Visor de Xml SAT",
    iconKey: "file",
    description: "Consulte ingresos, egresos y nóminas clasificados por cliente, periodo y reglas SAT.",
    testid: "nav-visor-de-xml-sat",
    component: <VisorCFDI />,
  }),
  createModuloTab({
    path: "/contabilidad",
    label: "Mis CFDI",
    iconKey: "file",
    description:
      "Contabilidad: tabla paginada de todos los CFDI en Mongo (manual y sync SAT), resumen por año y descarga XML/PDF.",
    testid: "nav-contabilidad",
    component: <Contabilidad />,
  }),
  createModuloTab({
    path: "/mis-cfdi",
    label: "Mis CFDI",
    iconKey: "file",
    description: "Alias de /contabilidad.",
    showInNav: false,
    testid: "nav-mis-cfdi",
    component: <Navigate to="/contabilidad" replace />,
  }),
  createModuloTab({
    path: "/gestionar-xml",
    label: "Gestionar XML",
    iconKey: "folder",
    description: "Redirige al visor — exportación unificada en Emitidos/Recibidos.",
    showInNav: false,
    testid: "nav-gestionar-xml",
    component: <Navigate to="/visores-sat?open=ingresos" replace />,
  }),
  createModuloTab({
    path: "/conciliacion-manual-cfdi",
    label: CONCILIACION_MANUAL_CFDI_TITLE,
    iconKey: "listchecks",
    description: "Concilie UUID entre Excel, CSV y complementos de pago cuando hay doble fuente.",
    testid: "nav-conciliacion-manual-cfdi",
    showInNav: false,
    hubCard: false,
    component: <Navigate to="/carga-informacion?open=conciliacion-manual" replace />,
  }),
  createModuloTab({
    path: "/auditoria-efos",
    label: "Auditoria CFDI (EFOS)",
    iconKey: "shield",
    description: "Revise listas EFOS/69-B y el riesgo fiscal de emisores y receptores en sus CFDI.",
    hubOnlyNav: true,
    hubCard: false,
    component: <AuditoriaEfos />,
  }),
  createModuloTab({
    path: "/auditoria-estatus-local",
    label: "Auditoría estatus local",
    iconKey: "shield",
    description: "Detecte CFDI cancelados en copia local sin confirmar en SAT y verifíquelos en lote.",
    hubOnlyNav: true,
    hubCard: false,
    component: <AuditoriaEstatusLocal />,
  }),
  createModuloTab({
    path: "/ayuda",
    label: "Ayuda y Manuales",
    iconKey: "help",
    hubOnlyNav: true,
    component: <Ayuda />,
  }),
  createModuloTab({
    path: "/exportacion-local",
    label: "Exportación local",
    iconKey: "folder",
    showInNav: false,
    hubOnlyNav: true,
    testid: "nav-exportacion-local",
    component: <Navigate to="/visores-sat?open=ingresos" replace />,
  }),
  createModuloTab({
    path: "/gestion-datos",
    label: "Gestión de Datos",
    iconKey: "database",
    hubOnlyNav: true,
    testid: "nav-gestion-datos",
    component: <GestionDatos />,
  }),
  createModuloTab({
    path: CONFIGURACION_DESPACHO_PATH,
    label: "Configuración del despacho",
    iconKey: "settings",
    showInNav: false,
    hubOnlyNav: true,
    description: "Perfil fiscal, licencia, usuarios y limpieza del espacio.",
    component: <Configuracion entryMode="despacho" />,
  }),
];

/** Rutas auxiliares (sin pestaña en sidebar; mismas reglas de enrutado). */
const VISOR_EXTRA_ROUTES = [
  { path: "visores-sat-xml", element: <Navigate to="/visores-sat" replace /> },
  { path: "visores-sat", element: <VisoresSatHub /> },
  { path: "visor-ingresos", element: <Navigate to="/visores-sat?open=ingresos" replace /> },
  { path: "visor-egresos", element: <Navigate to="/visores-sat?open=egresos" replace /> },
  { path: "visor-nominas", element: <Navigate to="/visores-sat?open=nominas" replace /> },
  { path: "confronta", element: <Navigate to="/confronta-cfdi?open=ingresos" replace /> },
  { path: "confronta-egresos", element: <Navigate to="/confronta-cfdi?open=egresos" replace /> },
  { path: "confronta-nominas", element: <Navigate to="/confronta-cfdi?open=nominas" replace /> },
  { path: "visor-confronta", element: <Navigate to="/visor" replace /> },
  { path: "carga-manual", element: <Navigate to="/carga-informacion" replace /> },
  { path: "carga-automatica", element: <Navigate to="/descarga-automatica-sat" replace /> },
  {
    path: "descarga-documentos-fiscales",
    element: <Navigate to="/documentos-fiscales-sat" replace />,
  },
  { path: "carga-excel", element: <Navigate to="/carga-informacion" replace /> },
  { path: "carga-csv", element: <Navigate to="/carga-informacion" replace /> },
  { path: "carga-plantilla-excel", element: <Navigate to="/carga-informacion" replace /> },
  { path: "carga-modo-complemento", element: <Navigate to="/carga-informacion?open=conciliacion-manual" replace /> },
];

export const MODULO_VISOR = buildModuloFromTabs({
  id: MODULO_VISOR_ID,
  card: MODULO_VISOR_CARD,
  contextLabel: CONTEXT_LABEL,
  tabs: VISOR_TABS,
  extraRoutes: VISOR_EXTRA_ROUTES,
});

export const getModuloVisorNavItems = () => MODULO_VISOR.getNavItems();
export const getModuloVisorPaths = () => MODULO_VISOR.getPaths();
export const getModuloVisorDefaultRoute = () => MODULO_VISOR.getDefaultRoute();
export const isModuloVisorPath = (pathname) => MODULO_VISOR.isPathAllowed(pathname);
export const getModuloVisorContextLabel = () => MODULO_VISOR.getContextLabel();
export const getModuloVisorRoutes = () => MODULO_VISOR.getRoutes();
